import React, { memo, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import styled from 'styled-components';

import { Icon, IconNames } from 'atoms/Icon';
import fontService, { FontWeight } from 'services/FontService';
import { CLIENT_WIDTH, desktop } from 'services/ui';
import { useOutsideClick, useWindowSize } from 'services/hooks';
import { Colors, UserRole } from 'context';
import { UserRoleSwitcher } from 'molecules/UserRoleSwitcher';
import i18n from '../../../i18n';

export interface IUserCard {
  name: string;
  email?: string;
  role: string;
  avatarImage?: string | null;
  hasRoles: UserRole[];
  onChangeRole: (role: UserRole) => void;
}

export enum UserCardRole {
  Teacher,
  Student,
  Methodist,
}

export const getUserRoleTranslated = (role: UserCardRole): string =>
  ({
    [UserCardRole.Teacher]: i18n.t('menu.user.teacher'),
    [UserCardRole.Student]: i18n.t('menu.user.student'),
    [UserCardRole.Methodist]: i18n.t('menu.user.methodist'),
  }[role]);

export const UserCard = memo(
  ({ name, email, role, hasRoles, avatarImage, onChangeRole }: IUserCard) => {
    const { t } = useTranslation();
    const { width } = useWindowSize();
    const [isOpened, setIsOpened] = useState<boolean>(false);

    const handleClickOutside = () => {
      setIsOpened(false);
    };

    const ref = useOutsideClick(handleClickOutside);

    const onOpenSwitcher = useCallback(() => {
      if (width && width >= CLIENT_WIDTH.DESKTOP) {
        return hasRoles.length > 1 && setIsOpened((prevState) => !prevState);
      }
      setIsOpened((prevState) => !prevState);
    }, [hasRoles, width]);

    const onChangeRoleClick = useCallback(
      (userRole: UserRole) => {
        onChangeRole(userRole);
        setIsOpened(false);
      },
      [onChangeRole],
    );

    return (
      <Wrapper>
        <ClickWrapper onClick={onOpenSwitcher} ref={ref}>
          <AvatarWrapper>
            {avatarImage ? (
              <AvatarImage src={avatarImage} alt={t('menu.user.avatarAlt')} />
            ) : (
              <Icon name={IconNames.Avatar} width="100%" height="100%" />
            )}
          </AvatarWrapper>
          <Info hasSeveralRoles={hasRoles.length > 1}>
            <UserInfo>
              {name}
              {email && <InfoEmail>{email}</InfoEmail>}
              <InfoRole>
                {getUserRoleTranslated(UserCardRole[role as keyof typeof UserCardRole])}
              </InfoRole>
            </UserInfo>

            <OpenButton>
              <Icon
                name={isOpened ? IconNames.TriangleUp : IconNames.TriangleDown}
                width={12}
                height={12}
              />
            </OpenButton>
          </Info>
        </ClickWrapper>

        {isOpened && (
          <UserRoleSwitcherStyled
            name={name}
            roles={hasRoles}
            activeRole={role as UserRole}
            onChangeRole={onChangeRoleClick}
          />
        )}
      </Wrapper>
    );
  },
);

const Wrapper = styled.div`
  position: relative;
`;

const ClickWrapper = styled.div`
  display: flex;
  cursor: pointer;
`;

const AvatarWrapper = styled.div`
  width: 36px;
  height: 36px;
  overflow: hidden;
  border-radius: 50%;

  ${desktop} {
    margin-right: 12px;
  }
`;

const AvatarImage = styled.img`
  width: 36px;
  height: 36px;
  object-fit: contain;
  object-position: center;
`;

const Info = styled.div<{ hasSeveralRoles: boolean }>`
  cursor: ${({ hasSeveralRoles }) => hasSeveralRoles && 'pointer'};
  display: flex;
  ${fontService.text({ size: 12, lineHeight: 150, weight: FontWeight.medium })}

  & button {
    ${desktop} {
      display: ${({ hasSeveralRoles }) => !hasSeveralRoles && 'none'};
    }
  }
`;

const UserInfo = styled.div`
  display: none;

  ${desktop} {
    display: block;
  }
`;

const InfoEmail = styled.div`
  ${fontService.text({ size: 11, lineHeight: 150, weight: FontWeight.regular })}
  /* Colors.gray в светлой теме это #ACB1B6 — 2.16:1 на белом при норме
     4.5. Это цвет РАЗДЕЛИТЕЛЕЙ, а не текста; вторичный текст красится
     Colors.gray_dark (#6B6C71 светлая / #B0B5BC тёмная). */
  color: ${Colors.gray_dark};
`;

const InfoRole = styled.div`
  ${fontService.text({ size: 12, lineHeight: 150, weight: FontWeight.regular })}
  /* Colors.gray в светлой теме это #ACB1B6 — 2.16:1 на белом при норме
     4.5. Это цвет РАЗДЕЛИТЕЛЕЙ, а не текста; вторичный текст красится
     Colors.gray_dark (#6B6C71 светлая / #B0B5BC тёмная). */
  color: ${Colors.gray_dark};
`;

const OpenButton = styled.button`
  cursor: pointer;
  background: transparent;
  border: none;
  outline: none;
  justify-self: flex-end;
  padding: 0;
  margin-left: 8px;

  ${desktop} {
    margin-left: 16px;
  }
`;

const UserRoleSwitcherStyled = styled(UserRoleSwitcher)`
  position: absolute;
  z-index: 111;
  top: 44px;
  right: 0;
`;
